import { Modal } from '@/components/ui';
import { CheckCircle, XCircle, ExternalLink, Headphones, FileText } from 'lucide-react';
import { useEffect, useState } from 'react';

// HistorialContexto read-only (same style as RevisionFieldModal.tsx)
interface HistorialEntry {
  id: number;
  estado: string;
  observacion?: string | null;
  adminNombre?: string | null;
  fecha: string;
}

function HistorialContexto({ historialList }: { historialList?: HistorialEntry[] }) {
  const list = historialList ?? [];
  if (list.length === 0) return null;

  const sorted = [...list].sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());

  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-3 max-h-60 overflow-y-auto space-y-2">
      <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-2">
        Contexto de revisión (Historial de conversación)
      </div>
      {sorted.map((e, idx) => {
        if (e.estado === 'RESPUESTA_ASESOR') {
          return (
            <div
              key={e.id ?? idx}
              className="p-2 rounded-lg bg-amber-50 border border-amber-200 text-[12px] text-amber-800 leading-relaxed"
            >
              <span className="font-semibold">
                Respuesta del asesor (
                {new Date(e.fecha).toLocaleString('es-CO', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })}
                ):
              </span>{' '}
              <span className="italic text-amber-950 whitespace-pre-wrap wrap-break-word">"{e.observacion}"</span>
            </div>
          );
        }

        if (e.estado.startsWith('SISTEMA')) {
          return (
            <div key={e.id ?? idx} className="p-1.5 rounded-lg bg-gray-100 text-[11px] text-gray-500 italic">
              {e.observacion || 'Actualización de archivo'} (
              {new Date(e.fecha).toLocaleString('es-CO', {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })}
              )
            </div>
          );
        }

        const isAprobado = e.estado === 'APROBADO';
        const isRechazado = e.estado === 'RECHAZADO';
        return (
          <div key={e.id ?? idx} className="p-2 rounded-lg bg-white border border-gray-200 space-y-0.5">
            <div className="flex items-center justify-between gap-2">
              <span
                className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold border ${
                  isAprobado
                    ? 'bg-[#eaf5ee] text-[#2d7a4f] border-[#b7dfc7]'
                    : isRechazado
                      ? 'bg-[#fdf0ee] text-[#c0392b] border-[#f5c6c0]'
                      : 'bg-[#eff6ff] text-[#1d4ed8] border-[#bfdbfe]'
                }`}
              >
                {e.adminNombre ? `${e.adminNombre} · ` : ''}
                {isAprobado ? 'Aprobó' : isRechazado ? 'Rechazó' : 'Comentó'}
              </span>
              <span className="text-[10px] text-gray-400">
                {new Date(e.fecha).toLocaleString('es-CO', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })}
              </span>
            </div>
            {e.observacion && (
              <span
                className={`text-[12px] block italic whitespace-pre-wrap wrap-break-word mt-1 ${
                  isAprobado ? 'text-[#2d7a4f]' : isRechazado ? 'text-[#c0392b]' : 'text-[#1d4ed8]'
                }`}
              >
                "{e.observacion}"
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export interface RevisionFileModalProps {
  open: boolean;
  onClose: () => void;
  titulo: string;
  kind: 'evidencia' | 'audio';
  descripcion?: string | null;
  fileUrl?: string | null;
  audioDuration?: number | null;
  historialList?: HistorialEntry[];
  estadoActual?: 'APROBADO' | 'RECHAZADO';
  observacionActual?: string;
  requiereResubirActual?: boolean;
  onAprobar?: (observacion?: string) => Promise<void>;
  onRechazar?: (observacion?: string, requiereResubir?: boolean) => Promise<void>;
  onAcceso?: () => void;
}

export function RevisionFileModal({
  open,
  onClose,
  titulo,
  kind,
  descripcion,
  fileUrl,
  audioDuration,
  historialList,
  estadoActual,
  observacionActual,
  requiereResubirActual,
  onAprobar,
  onRechazar,
  onAcceso,
}: RevisionFileModalProps) {
  const [comentario, setComentario] = useState('');
  const [requiereResubir, setRequiereResubir] = useState(true);
  const [guardando, setGuardando] = useState<'aprobar' | 'rechazar' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset textarea on open (do not prefill with old rejection text)
  useEffect(() => {
    if (open) {
      setComentario('');
      setRequiereResubir(requiereResubirActual ?? true);
      setError(null);
    }
  }, [open, requiereResubirActual]);

  const handleAprobar = async () => {
    setGuardando('aprobar');
    setError(null);
    try {
      await onAprobar?.(comentario.trim() || undefined);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setGuardando(null);
    }
  };

  const handleRechazar = async () => {
    setGuardando('rechazar');
    setError(null);
    try {
      await onRechazar?.(comentario.trim() || undefined, requiereResubir);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setGuardando(null);
    }
  };

  const footer = (
    <div className="space-y-2 font-['DM_Sans']">
      {error && <p className="text-[12px] text-red-600">{error}</p>}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={guardando !== null}
          className="px-3 py-1.5 text-[12px] font-semibold bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          Cerrar
        </button>

        {onRechazar && (
          <button
            type="button"
            onClick={handleRechazar}
            disabled={guardando !== null}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg border transition-colors disabled:opacity-50 ${
              estadoActual === 'RECHAZADO'
                ? 'bg-[#fdf0ee] text-[#c0392b] border-[#f5c6c0]'
                : 'bg-white text-[#c0392b] border-[#f5c6c0] hover:bg-[#fdf0ee]'
            }`}
          >
            <XCircle className="w-4 h-4" />
            {guardando === 'rechazar' ? 'Guardando...' : 'Rechazar'}
          </button>
        )}

        {onAprobar && (
          <button
            type="button"
            onClick={handleAprobar}
            disabled={guardando !== null}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg border transition-colors disabled:opacity-50 ${
              estadoActual === 'APROBADO'
                ? 'bg-[#eaf5ee] text-[#2d7a4f] border-[#b7dfc7]'
                : 'bg-white text-[#2d7a4f] border-[#b7dfc7] hover:bg-[#eaf5ee]'
            }`}
          >
            <CheckCircle className="w-4 h-4" />
            {guardando === 'aprobar' ? 'Guardando...' : 'Aprobar'}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <Modal isOpen={open} onClose={onClose} title={titulo} size="lg" footer={footer}>
      <div className="space-y-4 font-['DM_Sans']">
        {/* Contenido / Info del Archivo */}
        <div className="p-3.5 rounded-xl border border-gray-100 bg-gray-50/50 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 shrink-0 mt-0.5">
            {kind === 'audio' ? <Headphones className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-[13px] font-bold text-gray-900 truncate">{titulo}</h4>
            {descripcion && <p className="text-[12px] text-gray-500 mt-0.5 leading-relaxed">{descripcion}</p>}
            {kind === 'audio' && audioDuration != null && (
              <p className="text-[11px] text-gray-400 mt-0.5">
                Duración: {Math.floor(audioDuration / 3600) > 0 ? `${Math.floor(audioDuration / 3600)}h ` : ''}
                {Math.floor((audioDuration % 3600) / 60)}m {audioDuration % 60}s
              </p>
            )}

            <div className="flex items-center gap-2 mt-2">
              {fileUrl && (
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[11px] font-semibold bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <ExternalLink className="w-3 h-3" /> Ver archivo
                </a>
              )}
              {onAcceso && (
                <button
                  type="button"
                  onClick={onAcceso}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[11px] font-semibold bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  Obtener acceso
                </button>
              )}
            </div>
          </div>
        </div>

        {/* HistorialContexto */}
        <HistorialContexto historialList={historialList} />

        {/* Campo de Comentario */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5 block">Observación (opcional)</label>
          <textarea
            autoFocus
            rows={3}
            className="w-full px-3 py-2 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300 bg-white resize-none text-gray-800"
            placeholder="Escribe un comentario opcional para el asesor..."
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
          />
        </div>

        {/* Checkbox requiereResubir (solo para rechazo) */}
        <div>
          <label className="flex items-center gap-2 text-[12px] text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={requiereResubir}
              onChange={(e) => setRequiereResubir(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span>
              {kind === 'audio' ? 'Requiere volver a grabar/subir el audio' : 'Requiere volver a subir el archivo'}{' '}
              <span className="text-gray-400">(si lo desmarcas, basta con una aclaración)</span>
            </span>
          </label>
        </div>
      </div>
    </Modal>
  );
}
